import React, { memo, useCallback, useMemo } from 'react';
import { IconButton, ListItemIcon } from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import CheckIcon from '@mui/icons-material/Check';
import SortIcon from '@mui/icons-material/Sort';
import { MenuButton, MenuButtonItem } from 'components/menu-button';
import { rowMenuProps } from 'utils/row-menu';
import { useTranslate } from 'hooks/use-translate';
import { IDataGridMadel } from '../model';
import { findSortColumn } from './card';

interface Props {
  model: IDataGridMadel;
  sortable: { field: string; order: string };
  onTasks: (tasks: any[]) => void;
}

type SortOrder = 'asc' | 'desc';

/**
 * N2 (T2, DC-16 reversed): the card list's sort, as one icon button in the
 * grid's toolbar that opens a menu -- below md, on a grid that renders cards
 * and has sortable columns. It replaces the full-width sort row `CardList`
 * drew above the list (N1), and it is the same control: the same sortable
 * columns (the row's list), the same sort state (`sortable`) and the same
 * tasks, so the request is unchanged.
 *
 * The menu: the sortable fields with a check on the current one, then the
 * ascending / descending pair with a check on the current order. With no
 * current sort among the sortable columns (DC-14: the clinic drugs grid sorts
 * by a column that is not sortable) no field is checked -- the row showed "—".
 * The order pair acts on `sortable.field` exactly as the row's order button did.
 */
export const CardSortButton = memo<Props>(({ model, sortable, onTasks }) => {
  const { t } = useTranslate();
  const sortableColumns = useMemo(
    () => model.columns.filter((column) => Boolean(column.sortable)),
    [model.columns],
  );
  const order: SortOrder = sortable.order === 'asc' ? 'asc' : 'desc';
  const sortColumn = useMemo(
    () => findSortColumn(sortableColumns, sortable.field),
    [sortableColumns, sortable.field],
  );

  const onSortField = useCallback(
    (field: string) => {
      onTasks([['sortable', { field, order }], ['pagination', { page: 1 }], ['getData']]);
    },
    [onTasks, order],
  );
  const onSortOrder = useCallback(
    (value: SortOrder) => {
      if (!sortable.field || value === order) {
        return;
      }
      onTasks([
        ['sortable', { field: sortable.field, order: value }],
        ['pagination', { page: 1 }],
        ['getData'],
      ]);
    },
    [onTasks, sortable.field, order],
  );

  const items = useMemo<MenuButtonItem[]>(() => {
    const check = (on: boolean) => (
      <ListItemIcon>{on && <CheckIcon fontSize={'small'} />}</ListItemIcon>
    );
    const fields: MenuButtonItem[] = sortableColumns.map((column, i) => {
      const isCurrent = sortColumn === column;
      return {
        selected: isCurrent,
        divider: i === sortableColumns.length - 1,
        title: (
          <>
            {check(isCurrent)}
            {typeof column.title === 'string' ? t(column.title) : column.title}
          </>
        ),
        // the Select this replaces fired only on a change: re-picking the
        // current field sends nothing
        onClick: isCurrent ? undefined : () => onSortField(column.field),
      };
    });
    const orders: MenuButtonItem[] = (['asc', 'desc'] as SortOrder[]).map((value) => {
      const isCurrent = Boolean(sortable.field) && order === value;
      return {
        selected: isCurrent,
        disabled: !sortable.field,
        'aria-label': t(value === 'asc' ? 'ascending' : 'descending'),
        title: (
          <>
            {check(isCurrent)}
            {value === 'asc' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
          </>
        ),
        onClick: () => onSortOrder(value),
      };
    });
    return [...fields, ...orders];
  }, [sortableColumns, sortColumn, sortable.field, order, t, onSortField, onSortOrder]);

  if (sortableColumns.length === 0) {
    return null;
  }

  return (
    <MenuButton items={items} MenuProps={rowMenuProps('card', 'data-grid-card-menu')}>
      <IconButton
        className={'data-grid__toolbar__sort'}
        aria-label={t('sort')}
        aria-haspopup={'true'}
        color={'primary'}
      >
        <SortIcon />
      </IconButton>
    </MenuButton>
  );
});
